/**
 * Minimal 3-vector maths.
 *
 * Vectors are plain 3-element number arrays rather than objects or classes: the
 * integrators touch these millions of times per simulated flight, and packed
 * arrays avoid both property lookups and megamorphic call sites. Every helper
 * that allocates has an in-place `*Into` sibling for use inside hot loops.
 */

export type Vec3 = [number, number, number];
export type ReadonlyVec3 = readonly [number, number, number];

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => [x, y, z];

export const clone = (a: ReadonlyVec3): Vec3 => [a[0], a[1], a[2]];

export const add = (a: ReadonlyVec3, b: ReadonlyVec3): Vec3 => [
  a[0] + b[0],
  a[1] + b[1],
  a[2] + b[2],
];

export const sub = (a: ReadonlyVec3, b: ReadonlyVec3): Vec3 => [
  a[0] - b[0],
  a[1] - b[1],
  a[2] - b[2],
];

export const scale = (a: ReadonlyVec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];

export const negate = (a: ReadonlyVec3): Vec3 => [-a[0], -a[1], -a[2]];

/** a + b*s — the single most common operation in an integrator stage. */
export const addScaled = (a: ReadonlyVec3, b: ReadonlyVec3, s: number): Vec3 => [
  a[0] + b[0] * s,
  a[1] + b[1] * s,
  a[2] + b[2] * s,
];

export const dot = (a: ReadonlyVec3, b: ReadonlyVec3): number =>
  a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export const cross = (a: ReadonlyVec3, b: ReadonlyVec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

export const normSq = (a: ReadonlyVec3): number => a[0] * a[0] + a[1] * a[1] + a[2] * a[2];

export const norm = (a: ReadonlyVec3): number => Math.sqrt(normSq(a));

export const distance = (a: ReadonlyVec3, b: ReadonlyVec3): number => norm(sub(a, b));

/** Unit vector. Returns the zero vector for a zero-length input rather than NaNs. */
export function normalize(a: ReadonlyVec3): Vec3 {
  const n = norm(a);
  return n === 0 ? [0, 0, 0] : [a[0] / n, a[1] / n, a[2] / n];
}

/** Component-wise linear interpolation. Named to avoid colliding with the scalar `lerp` in `units.ts`. */
export const lerpVec3 = (a: ReadonlyVec3, b: ReadonlyVec3, t: number): Vec3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

/** Angle between two vectors in radians, clamped against floating-point overshoot. */
export function angleBetween(a: ReadonlyVec3, b: ReadonlyVec3): number {
  const d = dot(normalize(a), normalize(b));
  return Math.acos(Math.min(1, Math.max(-1, d)));
}

/** Component of `a` parallel to `b`. */
export function projectOnto(a: ReadonlyVec3, b: ReadonlyVec3): Vec3 {
  const bb = normSq(b);
  return bb === 0 ? [0, 0, 0] : scale(b, dot(a, b) / bb);
}

/** Component of `a` perpendicular to `b`. */
export const rejectFrom = (a: ReadonlyVec3, b: ReadonlyVec3): Vec3 => sub(a, projectOnto(a, b));

export function rotateX(a: ReadonlyVec3, angle: number): Vec3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [a[0], c * a[1] - s * a[2], s * a[1] + c * a[2]];
}

export function rotateY(a: ReadonlyVec3, angle: number): Vec3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [c * a[0] + s * a[2], a[1], -s * a[0] + c * a[2]];
}

export function rotateZ(a: ReadonlyVec3, angle: number): Vec3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [c * a[0] - s * a[1], s * a[0] + c * a[1], a[2]];
}

/** Rotation of `a` about an arbitrary unit `axis` by `angle` (Rodrigues' formula). */
export function rotateAbout(a: ReadonlyVec3, axis: ReadonlyVec3, angle: number): Vec3 {
  const k = normalize(axis);
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const kXa = cross(k, a);
  const kDa = dot(k, a);
  return [
    a[0] * c + kXa[0] * s + k[0] * kDa * (1 - c),
    a[1] * c + kXa[1] * s + k[1] * kDa * (1 - c),
    a[2] * c + kXa[2] * s + k[2] * kDa * (1 - c),
  ];
}

export const isFinite3 = (a: ReadonlyVec3): boolean =>
  Number.isFinite(a[0]) && Number.isFinite(a[1]) && Number.isFinite(a[2]);
